// file: `src/Components/context/RevealContext.jsx`
import React, { createContext, useRef, useCallback, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import { useLenis } from 'lenis/react';

export const RevealContext = createContext({
    resetReveal: () => {}
});

export function RevealProvider({ lang, started, children }) {
    const lenis = useLenis();
    const splitsRef = useRef([]);
    const timelinesRef = useRef([]);
    const scrollerRef = useRef(null);

    const cleanup = useCallback(() => {
        timelinesRef.current.forEach((tl) => {
            try { tl.kill(); } catch (e) { /* ignore */ }
        });
        timelinesRef.current = [];

        splitsRef.current.forEach((s) => {
            try { s.revert(); } catch (e) { /* ignore */ }
        });
        splitsRef.current = [];

        try { ScrollTrigger.getAll().forEach(st => st.kill()); } catch (e) { /* ignore */ }
    }, []);

    const debounce = (fn, wait = 150) => {
        let t;
        return (...args) => {
            clearTimeout(t);
            t = setTimeout(() => fn(...args), wait);
        };
    };

    const initReveal = useCallback(async () => {
        if (typeof document === 'undefined') return;

        // Wait for fonts to be ready (Safari/Chrome support document.fonts.ready).
        // Use a small timeout fallback so we don't block indefinitely.
        try {
            if (document.fonts && document.fonts.ready) {
                await Promise.race([document.fonts.ready, new Promise((res) => setTimeout(res, 200))]);
            } else {
                await new Promise((res) => setTimeout(res, 150));
            }
        } catch (e) {
            // ignore and continue
        }

        // Attendre que Lenis ait traité sa position (scroll reset après navigation)
        await new Promise((res) => setTimeout(res, 50));

        if (scrollerRef.current && scrollerRef.current._cleanup) {
            try { scrollerRef.current._cleanup(); } catch (e) { /* ignore */ }
            try { delete scrollerRef.current._cleanup; } catch (e) { /* ignore */ }
        }

        cleanup();

        const scrollerEl = document.querySelector('.container') || document.scrollingElement || document.body;
        scrollerRef.current = scrollerEl;

        try { gsap.registerPlugin(ScrollTrigger); } catch (e) { /* already registered */ }

        if (lenis && scrollerEl) {
            ScrollTrigger.scrollerProxy(scrollerEl, {
                scrollTop(value) {
                    if (arguments.length) {
                        lenis.scrollTo(value, { immediate: true });
                        return;
                    }
                    return lenis.scroll || 0;
                },
                getBoundingClientRect() {
                    return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
                },
                pinType: scrollerEl.style.transform ? 'transform' : 'fixed'
            });

            const onLenisScroll = () => ScrollTrigger.update();
            lenis.on('scroll', onLenisScroll);

            const onRefresh = () => {
                try { ScrollTrigger.update(); } catch (e) { /* ignore */ }
            };
            ScrollTrigger.addEventListener('refresh', onRefresh);

            requestAnimationFrame(() => ScrollTrigger.refresh());

            scrollerRef.current._cleanup = () => {
                try { lenis.off('scroll', onLenisScroll); } catch (e) { /* ignore */ }
                try { ScrollTrigger.removeEventListener('refresh', onRefresh); } catch (e) { /* ignore */ }
            };
        }

        const els = Array.from(document.querySelectorAll('.reveal-type'));
        els.forEach((el) => {
            let split;
            try {
                split = new SplitType(el, { types: 'lines, words', tagName: 'span' });
            } catch (e) {
                return;
            }
            splitsRef.current.push(split);

            const words = el.querySelectorAll('.word');

            // set the container CSS variable for color and prepare words for animation
            try { gsap.set(el, { '--reveal-color': 'transparent' }); } catch (e) { /* ignore */ }
            gsap.set(words, { opacity: 0, y: 8, overwrite: true });

            const tl = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    scroller: scrollerRef.current,
                    start: 'top bottom-=100',
                    end: 'bottom bottom-=150',
                    scrub: 0.5,
                }
            });

            // animate words opacity + translateY for a smooth reveal
            tl.to(words, {
                opacity: 1,
                y: 0,
                stagger: 0.03,
                duration: 0.6,
                ease: 'power3.out'
            });

            // sync the visible color via the CSS variable on the container.
            // use a very short tween (or a set) so WebKit picks up -webkit-text-fill-color.
            try {
                tl.to(el, { '--reveal-color': '#ffffff', duration: 0.01 }, 0);
            } catch (e) {
                // fallback: immediate set if animating CSS var is not supported
                try { gsap.set(el, { '--reveal-color': '#ffffff' }); } catch (err) { /* ignore */ }
            }

            timelinesRef.current.push(tl);
        });

        requestAnimationFrame(() => {
            try { ScrollTrigger.refresh(); } catch (e) { /* ignore */ }
            setTimeout(() => {
                try { ScrollTrigger.refresh(); } catch (e) { /* ignore */ }
            }, 100);
        });
    }, [lenis, cleanup]);

    // initialise/re-init au montage, au changement de langue et à chaque changement de route.
    // On attend que la loading screen soit fermée (started) pour éviter les long tasks
    // pendant la mesure TBT de Lighthouse.
    useEffect(() => {
        if (!started) return;
        initReveal();
        window.addEventListener('load', initReveal);
        return () => {
            window.removeEventListener('load', initReveal);
            if (scrollerRef.current && scrollerRef.current._cleanup) {
                try { scrollerRef.current._cleanup(); } catch (e) { /* ignore */ }
            }
            cleanup();
        };
    }, [lang, initReveal, cleanup, started]);

    useEffect(() => {
        const onResize = debounce(() => {
            if (typeof document === 'undefined') return;

            if (scrollerRef.current && scrollerRef.current._cleanup) {
                try { scrollerRef.current._cleanup(); } catch (e) { /* ignore */ }
                try { delete scrollerRef.current._cleanup; } catch (e) { /* ignore */ }
            }

            cleanup();
            requestAnimationFrame(() => {
                initReveal();
                try { ScrollTrigger.refresh(); } catch (e) { /* ignore */ }
            });
        }, 150);

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [initReveal, cleanup]);

    const resetReveal = useCallback(() => {
        initReveal();
    }, [initReveal]);

    return (
        <RevealContext.Provider value={{ resetReveal }}>
            {children}
        </RevealContext.Provider>
    );
}
